import React, { useState } from "react";
import api, { formatTL, formatDateTime, formatDuration } from "@/lib/api";
import { useLiveQuery } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plate } from "@/components/Plate";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CarProfile, Clock, PlusCircle, X, Receipt } from "@phosphor-icons/react";

export default function BusinessHome() {
  const [plate, setPlate] = useState("");
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const { data, refetch, error } = useLiveQuery(
    "business_me",
    async () => (await api.get("/business/me")).data,
    { intervalMs: 5000 },
  );
  const { data: stmt } = useLiveQuery(
    "business_statement",
    async () => (await api.get(`/business/statement?month=${month}`)).data,
    { intervalMs: 30000, deps: [month] },
  );
  const { data: recv } = useLiveQuery(
    "business_receivables",
    async () => (await api.get("/business/receivables")).data,
    { intervalMs: 30000 },
  );

  const acc = data?.account;
  const report = async () => {
    const p = plate.replace(/\s+/g, "").toUpperCase();
    if (p.length < 4) return toast.error("Geçerli bir plaka girin");
    setBusy(true);
    try {
      const { data: r } = await api.post("/business/report-plate", { plate: p });
      if (r.mode === "tagged_active") {
        toast.success(r.business_over_quota
          ? `${p} kaydedildi — kontenjan dolu, normal tarife uygulanır`
          : `${p} otoparkta — müşteriniz olarak işaretlendi`);
      } else if (r.mode === "claim_created") {
        toast.success(`${p} bildirildi — araç girişte otomatik işaretlenecek (12 saat geçerli)`);
      } else {
        toast.info(`${p} zaten bekleyen bildirimlerde`);
      }
      setPlate("");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bildirilemedi"); }
    finally { setBusy(false); }
  };

  const cancelClaim = async (id) => {
    try { await api.delete(`/business/claims/${id}`); toast.success("Bildirim iptal edildi"); refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || "İptal edilemedi"); }
  };

  if (error) {
    return <Card className="rounded-sm border-destructive/40"><CardContent className="p-6 text-center text-sm text-destructive" data-testid="business-error">
      {error?.response?.data?.detail || "İşletme bilgisi yüklenemedi"}
    </CardContent></Card>;
  }
  if (!acc) return <div className="text-sm text-muted-foreground">Yükleniyor…</div>;

  const isQuota = acc.agreement_type === "quota";
  return (
    <div className="space-y-5 animate-fade-in">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">İşletme</div>
        <h1 className="text-3xl font-bold tracking-tight" data-testid="business-name">{acc.name}</h1>
      </div>

      <Card className={`rounded-sm border-2 ${acc.quota_full ? "border-red-500 bg-red-50/40" : "border-violet-300 bg-violet-50/40"}`}>
        <CardContent className="p-5 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">
              {isQuota ? "Kontenjan Kullanımı" : "Şu An İçerideki Müşteri Araçları"}
            </div>
            <div className="text-4xl font-bold font-mono mt-1" data-testid="business-quota-usage">
              {acc.active_count}{isQuota ? `/${acc.quota_slots}` : ""}
            </div>
            <div className="text-xs mt-1 text-muted-foreground">
              {isQuota
                ? (acc.quota_full ? "Kontenjan dolu — yeni araçlara normal tarife uygulanır" : `${acc.quota_slots - acc.active_count} yer boş`)
                : `Anlaşma: ilk ${acc.covered_hours} saat işletmeden (₺${acc.business_fee_per_vehicle}/araç)`}
            </div>
          </div>
          <CarProfile size={40} className={acc.quota_full ? "text-red-500" : "text-violet-500"} />
        </CardContent>
      </Card>

      {acc.agreement_type === "first_hours" && recv && (
        <Card className="rounded-sm border-2 border-orange-300 bg-orange-50/40">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Otoparka Ödenecek (İşletme Payı)</div>
                <div className="text-3xl font-bold font-mono text-orange-700 mt-1" data-testid="business-debt-total">{formatTL(recv.pending_total)}</div>
                <div className="text-xs text-muted-foreground">{recv.pending_count} araç bekliyor · tahsil edilen {formatTL(recv.collected_total)}</div>
              </div>
              <Receipt size={36} className="text-orange-500" />
            </div>
            {recv.pending.slice(0, 15).map((s) => (
              <div key={s.id} className="flex items-center justify-between text-xs border-t border-orange-200/60 pt-2" data-testid={`business-debt-${s.plate}`}>
                <Plate>{s.plate}</Plate>
                <span className="text-muted-foreground font-mono">{formatDateTime(s.exit_time)}</span>
                <span className="font-mono font-semibold text-orange-700">{formatTL(s.business_fee)}</span>
              </div>
            ))}
            <div className="text-[10px] text-muted-foreground">Bu liste bilgi amaçlıdır; tahsilat otopark yönetimi tarafından yapılır.</div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm border-border">
        <CardContent className="p-5 space-y-3">
          <div className="text-sm font-semibold uppercase tracking-widest flex items-center gap-2">
            <PlusCircle size={16} /> Müşteri Plakası Bildir
          </div>
          <div className="flex gap-2">
            <Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())}
                   onKeyDown={(e) => e.key === "Enter" && report()}
                   placeholder="34ABC123" className="rounded-sm h-12 font-plate text-lg uppercase"
                   data-testid="business-plate-input" />
            <Button onClick={report} disabled={busy} data-testid="business-report-btn"
                    className="rounded-sm h-12 bg-violet-600 hover:bg-violet-700 text-white uppercase tracking-widest font-semibold px-6">
              Bildir
            </Button>
          </div>
          <div className="text-[11px] text-muted-foreground">
            Araç otoparktaysa anında işaretlenir; henüz girmediyse girişte otomatik işaretlenir (12 saat geçerli).
          </div>
        </CardContent>
      </Card>

      {data.pending_claims.length > 0 && (
        <Card className="rounded-sm border-border">
          <CardContent className="p-0 divide-y divide-border">
            <div className="p-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Clock size={14} /> Girişi Bekleyen Bildirimler ({data.pending_claims.length})
            </div>
            {data.pending_claims.map((c) => (
              <div key={c.id} className="p-3 flex items-center justify-between" data-testid={`business-claim-${c.plate}`}>
                <Plate>{c.plate}</Plate>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-muted-foreground">Son: {formatDateTime(c.expires_at)}</span>
                  <button onClick={() => cancelClaim(c.id)} className="text-muted-foreground hover:text-destructive"
                          data-testid={`business-claim-cancel-${c.plate}`} aria-label="İptal"><X size={16} /></button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm border-border">
        <CardContent className="p-0 divide-y divide-border">
          <div className="p-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            İçerideki Müşteri Araçları ({data.active_sessions.length})
          </div>
          {data.active_sessions.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">Şu an içeride müşteri aracı yok</div>
          )}
          {data.active_sessions.map((s) => (
            <div key={s.id} className="p-3 flex items-center justify-between gap-2" data-testid={`business-active-${s.plate}`}>
              <div className="flex items-center gap-2">
                <Plate>{s.plate}</Plate>
                {s.business_over_quota && (
                  <Badge className="rounded-sm bg-red-600 text-white text-[9px] uppercase border-transparent">Kontenjan dışı</Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground font-mono">{formatDateTime(s.entry_time)}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="rounded-sm border-border">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-semibold uppercase tracking-widest flex items-center gap-2">
              <Receipt size={16} /> Aylık Hesap Özeti
            </div>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                   className="rounded-sm h-9 w-40" data-testid="business-month-input" />
          </div>
          {stmt && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-3 bg-muted rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Araç</div>
                <div className="text-xl font-bold font-mono" data-testid="business-stmt-count">{stmt.vehicle_count}</div>
              </div>
              <div className="p-3 bg-muted rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">İşletmeye</div>
                <div className="text-xl font-bold font-mono text-violet-700" data-testid="business-stmt-total">{formatTL(stmt.business_total)}</div>
              </div>
              <div className="p-3 bg-muted rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Müşteriden</div>
                <div className="text-xl font-bold font-mono">{formatTL(stmt.customer_total)}</div>
              </div>
            </div>
          )}
          {stmt?.items?.slice(0, 20).map((it) => (
            <div key={it.id} className="flex items-center justify-between text-xs border-t border-border pt-2">
              <Plate>{it.plate}</Plate>
              <span className="text-muted-foreground font-mono">{formatDateTime(it.entry_time)}</span>
              <span className="font-mono">{it.duration_minutes != null ? formatDuration(it.duration_minutes) : "içeride"}</span>
              <span className="font-mono font-semibold">{it.status === "closed" ? formatTL(it.fee) : "—"}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
